// The To do tab: urgent items, tasks people added, next steps grouped by what each job is waiting on, and recently done.
import type { AutoTask, StepKey, Task } from '@shared/index';
import { autoTasks, compareQuotes, isDone, money, NEXT_STEPS } from '@shared/index';
import { activePms, allStageItems, jobItems, store, today, ui } from '../store';
import { registerView, render } from '../render';
import { addTask } from '../actions';
import { $, el, pmSeg } from '../ui';
import { isLate, stageRow, taskRow, type Row } from './tasks';

export interface StepGroup { key: StepKey; label: string; items: AutoTask[]; left: number }

export interface TodoModel {
  /** Red badge on the tab: every urgent reminder and overdue task, whoever's filter is on. */
  badge: number;
  /** Open items per PM, for the PM buttons. */
  counts: Record<string, number>;
  urgent: Row[];
  manual: Task[];
  next: AutoTask[];
  groups: StepGroup[];
  done: Task[];
}

const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** Everything the To do tab shows, worked out without touching the page. */
export function todoModel(tasks: Task[], auto: AutoTask[], pm: string, now: string): TodoModel {
  const manualOpen: Row[] = tasks.filter((t) => !t.done);
  const late = (t: Row) => isLate(t, now);
  const badge = auto.filter((t) => t.level === 'urgent').length + manualOpen.filter(late).length;
  const counts: Record<string, number> = {};
  [...manualOpen, ...auto].forEach((t) => { counts[t.pm] = (counts[t.pm] || 0) + 1; });
  const mine = (t: { pm: string }) => !pm || t.pm === pm;
  const key = (t: Row) => (t.auto ? t.job.install_date : t.due) || '9999';
  const urgent = [...auto.filter((t) => t.level === 'urgent' && mine(t)), ...manualOpen.filter((t) => late(t) && mine(t))]
    .sort((a, b) => cmp(key(a), key(b)));
  const manual = (manualOpen as Task[]).filter((t) => mine(t) && !late(t))
    .sort((a, b) => cmp(a.due || '9999', b.due || '9999') || cmp(b.created_at || '', a.created_at || ''));
  const next = auto.filter((t) => t.level === 'next' && mine(t)).sort((a, b) => a.text.localeCompare(b.text) || compareQuotes(a.quote, b.quote));
  const groups = NEXT_STEPS.map(([k, , label]) => {
    const items = next.filter((t) => t.stage === k).sort((a, b) => a.pm.localeCompare(b.pm) || compareQuotes(a.quote, b.quote));
    return { key: k, label, items, left: items.reduce((t, x) => t + Number(x.job.remaining || 0), 0) };
  }).filter((g) => g.items.length);
  const done = tasks.filter((t) => t.done && mine(t)).sort((a, b) => cmp(b.done_at || '', a.done_at || '')).slice(0, 30);
  return { badge, counts, urgent, manual, next, groups, done };
}

const allAuto = () => [...jobItems(), ...allStageItems()].flatMap((j) => autoTasks(j, today()));

/** Job picker for the add form: the chosen PM's open jobs. */
function fillJobSelect() {
  const sel = $<HTMLSelectElement>('tdJob');
  const who = $<HTMLSelectElement>('tdWho').value;
  const cur = sel.value;
  sel.replaceChildren(new Option('No specific job', ''));
  store.jobs.filter((j) => j.pm === who && !isDone(j)).sort((a, b) => compareQuotes(a.quote, b.quote))
    .forEach((j) => sel.append(new Option(j.quote + ' · ' + j.address, j.quote)));
  if ([...sel.options].some((o) => o.value === cur)) sel.value = cur;
}

/** PM list for the add form, rebuilt only when the PMs change so a choice isn't lost. */
function fillWho() {
  const sel = $<HTMLSelectElement>('tdWho');
  const names = activePms();
  const sig = names.join('|');
  if (sel.dataset.sig === sig) return;
  const cur = sel.value;
  sel.replaceChildren(...names.map((n) => new Option(n, n)));
  sel.dataset.sig = sig;
  const want = [cur, ui.tdo.pm, store.profile?.pm_name || ''].find((n) => n && names.includes(n));
  if (want) sel.value = want;
}

function fill(id: string, L: Row[], emptyText: string) {
  const box = $(id);
  box.replaceChildren();
  if (!L.length) box.append(el('div', 'note-line', emptyText));
  L.forEach((t) => box.append(taskRow(t)));
}

function stepGroup(g: StepGroup): HTMLElement {
  const dt = el('details', 'stage');
  dt.open = ui.stageOpen.has(g.key);
  dt.addEventListener('toggle', () => { if (dt.open) ui.stageOpen.add(g.key); else ui.stageOpen.delete(g.key); });
  const sm = el('summary');
  sm.append(el('span', undefined, g.label), el('span', 'val', money(g.left) + ' left'), el('span', 'cnt', g.items.length));
  const body = el('div', 'tlist');
  g.items.forEach((t) => body.append(stageRow(t)));
  dt.append(sm, body);
  return dt;
}

function pickPm(pm: string) {
  ui.tdo.pm = pm;
  if (pm && activePms().includes(pm)) { $<HTMLSelectElement>('tdWho').value = pm; fillJobSelect(); }
  render(['todo']);
}

function renderTodo() {
  fillWho();
  // Viewers can't add tasks (the database would refuse), so don't offer the form.
  $('tdForm').closest('.panel')!.toggleAttribute('hidden', !store.canWrite);
  if (!store.loaded) { pmSeg($('tdPm'), ui.tdo.pm, pickPm, {}); return; }
  const m = todoModel(store.tasks, allAuto(), ui.tdo.pm, today());
  const badge = $('todoBadge');
  badge.hidden = !m.badge; badge.textContent = String(m.badge);
  pmSeg($('tdPm'), ui.tdo.pm, pickPm, m.counts);
  fill('tdUrgent', m.urgent, 'Nothing urgent.');
  fill('tdManual', m.manual, 'No open tasks. Add one above, or from inside a job.');
  const nb = $('tdNext');
  nb.replaceChildren();
  if (!m.next.length) nb.append(el('div', 'note-line', 'Every job is up to date.'));
  m.groups.forEach((g) => nb.append(stepGroup(g)));
  fill('tdDone', m.done, 'Nothing ticked off yet.');
  $('tdUrgN').textContent = String(m.urgent.length);
  $('tdManN').textContent = String(m.manual.length);
  $('tdNextN').textContent = String(m.next.length);
  $('tdDoneH').textContent = 'Done (' + m.done.length + ')';
  if (document.activeElement !== $('tdJob')) fillJobSelect();
}

export function initTodo() {
  $('tdWho').addEventListener('change', fillJobSelect);
  $<HTMLFormElement>('tdForm').addEventListener('submit', (e) => {
    e.preventDefault();
    const text = $<HTMLInputElement>('tdText');
    const due = $<HTMLInputElement>('tdDue');
    const v = text.value.trim();
    if (!v) return;
    addTask({ text: v, pm: $<HTMLSelectElement>('tdWho').value, quote: $<HTMLSelectElement>('tdJob').value || null, due: due.value || null });
    text.value = ''; due.value = '';
  });
  registerView('todo', renderTodo);
}
